<template>
    <div class="data-wrapper-core whatsapp-connections-wrapper">
        <template v-if="!hideHeader">
            <h3>WhatsApp Verbindungen</h3>
            <v-divider></v-divider>
        </template>
        <div v-if="loading" class="loading-container">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else-if="whatsAppConnectionStats && whatsAppConnectionStats.connections && whatsAppConnectionStats.connections.length > 0">
            <div class="whatsapp-stats-summary">
                <v-row>
                    <v-col cols="12" sm="6" md="3">
                        <div class="stat-box">
                            <h5>Gesamt Verbindungen:</h5>
                            <h3>{{ whatsAppConnectionStats.totalConnections }}</h3>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <div class="stat-box">
                            <h5>Aktive Verbindungen:</h5>
                            <h3>{{ whatsAppConnectionStats.activeConnections }}</h3>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <div class="stat-box">
                            <h5>Gesamt Nachrichten:</h5>
                            <h3>{{ whatsAppConnectionStats.totalMessages }}</h3>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <div class="stat-box">
                            <h5>Gesamt Konversationen:</h5>
                            <h3>{{ whatsAppConnectionStats.totalConversations }}</h3>
                        </div>
                    </v-col>
                </v-row>
            </div>
            <v-text-field 
                class="whatsapp-connections-search-field" 
                prepend-inner-icon="mdi-magnify" 
                variant="outlined"
                density="compact" 
                label="Suche" 
                hide-details 
                single-line 
                rounded="lg" 
                v-model="searchQuery"
            ></v-text-field>
            <v-expansion-panels>
                <v-expansion-panel 
                    rounded="lg" 
                    v-for="connection in filteredConnections" 
                    :key="connection._id"
                >
                    <v-expansion-panel-title class="whatsapp-connections-panel-title">
                        <div class="connection-header">
                            <h4>{{ connection.name || 'Unbenannte Verbindung' }}</h4>
                            <v-chip 
                                :color="getStatusColor(connection.status)" 
                                size="small"
                                class="status-chip"
                            >
                                {{ connection.status }}
                            </v-chip>
                        </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="whatsapp-connections-panel-text">
                        <v-list density="compact">
                            <h4>Verbindungs-ID:</h4>
                            <v-list-item>{{ connection._id }}</v-list-item>
                            
                            <h4>Business Account ID:</h4>
                            <v-list-item>{{ connection.businessAccountId || "--" }}</v-list-item>
                            
                            <h4>Status:</h4>
                            <v-list-item>
                                <v-chip :color="getStatusColor(connection.status)" size="small">
                                    {{ connection.status }}
                                </v-chip>
                            </v-list-item>
                            
                            <h4>Gültig:</h4>
                            <v-list-item>
                                <span :style="{ color: connection.valid ? 'green' : 'red' }">
                                    {{ connection.valid ? 'Ja' : 'Nein' }}
                                </span>
                            </v-list-item>
                            
                            <h4>Erstellt am:</h4>
                            <v-list-item>{{ formatDate(connection.createdAt) }}</v-list-item>
                            
                            <h4>Ablaufdatum:</h4>
                            <v-list-item>
                                <span :style="{ color: isExpired(connection.expireDate) ? 'red' : '' }">
                                    {{ formatDate(connection.expireDate) }}
                                </span>
                            </v-list-item>
                            
                            <h4>Aktive Automatisierungen:</h4>
                            <v-list-item>{{ connection.activeAutomations }}</v-list-item>
                            
                            <h4>Anzahl Nachrichten:</h4>
                            <v-list-item>{{ connection.messageCount }}</v-list-item>
                            
                            <h4>Anzahl Konversationen:</h4>
                            <v-list-item>{{ connection.conversationCount }}</v-list-item>
                            
                            <div v-if="connection.phoneNumbers && connection.phoneNumbers.length > 0" class="phone-numbers-section">
                                <h4>Telefonnummern ({{ connection.phoneNumbers.length }}):</h4>
                                <v-divider></v-divider>
                                <div 
                                    v-for="phoneNumber in connection.phoneNumbers" 
                                    :key="phoneNumber._id"
                                    class="phone-number-item"
                                >
                                    <h5>Nummer:</h5>
                                    <v-list-item>{{ phoneNumber.phoneNumber || "--" }}</v-list-item>
                                    <h5>Name:</h5>
                                    <v-list-item>{{ phoneNumber.name || "--" }}</v-list-item>
                                    <h5>Qualitätsbewertung:</h5>
                                    <v-list-item>{{ phoneNumber.qualityRating || "--" }}</v-list-item>
                                    <h5>Verifizierungsstatus:</h5>
                                    <v-list-item>{{ phoneNumber.codeVerificationStatus || "--" }}</v-list-item>
                                    <v-divider class="phone-number-divider"></v-divider>
                                </div>
                            </div>
                            <div v-else>
                                <h4>Telefonnummern:</h4>
                                <v-list-item>Keine Telefonnummern zugeordnet</v-list-item>
                            </div>
                        </v-list>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div v-else class="no-connections">
            <p>Keine WhatsApp Verbindungen gefunden</p>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    props: {
        hideHeader: { type: Boolean, default: false }
    },
    data() {
        return {
            searchQuery: '',
        };
    },
    computed: {
        ...mapState('agency', ['whatsAppConnectionStats', 'loading']),
        filteredConnections() {
            if (!this.whatsAppConnectionStats || !this.whatsAppConnectionStats.connections) {
                return [];
            }
            if (!this.searchQuery) {
                return this.whatsAppConnectionStats.connections;
            }
            const query = this.searchQuery.toLowerCase();
            return this.whatsAppConnectionStats.connections.filter(connection => 
                (connection.name && connection.name.toLowerCase().includes(query)) ||
                (connection.businessAccountId && connection.businessAccountId.toLowerCase().includes(query)) ||
                connection.status.toLowerCase().includes(query)
            );
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return '--';
            return new Date(date).toLocaleDateString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        isExpired(expireDate) {
            if (!expireDate) return false;
            return new Date(expireDate) < new Date();
        },
        getStatusColor(status) {
            switch (status) {
                case 'gut':
                    return 'success';
                case 'Fehler':
                    return 'error';
                case 'Abgelaufen':
                    return 'warning';
                default:
                    return 'grey';
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.whatsapp-connections-wrapper {
    max-height: 800px;
    overflow-y: auto;
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
}

.whatsapp-stats-summary {
    margin: 16px 0;
    padding: 16px;
    background-color: #f9fafb;
    border-radius: 12px;
}

.stat-box {
    text-align: center;
    padding: 12px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    h5 {
        margin: 0 0 8px 0;
        font-size: 0.875rem;
        color: #666;
        font-weight: 500;
    }
    
    h3 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
        color: #333;
    }
}

.whatsapp-connections-search-field {
    margin: 8px 0;
}

.connection-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    
    h4 {
        margin: 0;
    }
}

.status-chip {
    margin-left: 8px;
}

.whatsapp-connections-panel-text {
    padding: 0px 16px;
}

.phone-numbers-section {
    margin: 24px auto;
    padding: 12px;
    border: 1px solid rgb(196, 196, 196);
    border-radius: 8px;
}

.phone-number-item {
    margin-bottom: 16px;
    
    &:last-child {
        margin-bottom: 0;
    }
}

.phone-number-divider {
    margin: 12px 0;
}

.no-connections {
    text-align: center;
    padding: 40px;
    color: #666;
}

</style>

